<template>
    <div :style="styles">
        <multiselect
            v-model="selectedObjects"
            :searchable="searchable"
            :track-by="nameKey"
            :value-prop="valueKey"
            :id="id"
            :object="typeof options[0] == 'object'"
            :label="labelKey"
            :mode="multiple ? 'tags' : 'single'"
            :disabled="disabled"
            :close-on-select="!multiple"
            :hide-selected="multiple"
            :allow-empty="allowEmpty"
            :can-clear="false"
            :options="options"
            :placeholder="placeholder"
            :show-labels="false"
            :required="required"
            :groups="groups"
            :limit="optionsLimit"
            @select="onChange()"
            @deselect="onChange()"
            @clear="onClear()"
            @close="onClose()"
        >
            <template #option="props">
                <slot name="option">
                    <template v-if="spanKey !== null && props.option[spanKey]">
                        <span class="label label-default">
                            {{ props.option[spanKey] }}
                        </span>
                    </template>
                    {{ valueToLabel(props.option) }}
                </slot>
            </template>
        </multiselect>

        <template v-if="multiple">
            <input
                v-for="item in selectedValues"
                :key="item"
                :name="name"
                :value="item"
                type="hidden"
            >
        </template>
        <input
            v-else
            type="hidden"
            :name="name"
            :value="selectedValues"
        >
    </div>
</template>

<script>
/*
       Usage:
       <dropdown
       :options="[{id: 'foo', value: 'baar'}]"
       label-key="value" value-key="id"
       :value="myValue"
       :multiple="false"
       v-on:input="myValue=arguments[0]"
       ></dropdown>

     */
// import Multiselect from 'vue-multiselect';
import Multiselect from '@vueform/multiselect';

export default {
    components: {
        Multiselect,
    },
    props: {
        options: { default: () => [] },
        name: { default: null },
        id: { default: null },
        modelValue: { default: null },
        value: { default: null },
        labelKey: String,
        valueKey: {
            default: null,
        },
        multiple: { default: false },
        placeholder: {
            default: 'Select',
        },
        spanKey: { default: null },
        searchable: {
            type: Boolean,
            default: true,
        },
        required: {
            type: Boolean,
            required: false,
            default: false,
        },
        groups: {
            type: Boolean,
            required: false,
            default: false,
        },
        optionsLimit: {
            type: Number,
            default: 1000,
            required: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        allowEmpty: {
            type: Boolean,
            default: true,
        },
        styles: {
            type: String,
            default: '',
        },
        canClear: {
            type: Boolean,
            default: true,
        },
        nameKey: {
            type: String,
        },
    },
    emits: ['update:modelValue', 'input', 'object', 'close'],

    data() {
        return {
            selectedObjects: null,
        }
    },

    computed: {
        selectedValues() {
            if (this.multiple && this.selectedObjects) {
                return this.selectedObjects.map(this.valueToKey.bind(this));
            }

            if (this.selectedObjects) {
                const retval = this.valueToKey(this.selectedObjects);
                return retval;
            }

            return null;
        },
        selectedValueObjects() {
            if (this.selectedObjects) {
                return this.selectedObjects;
            }

            return null;
        },
    },
    watch: {
        value() {
            this.updateSelected();
        },
        modelValue() {
            this.updateSelected();
        },
        options() {
            this.updateSelected();
        }
    },
    mounted() {
        this.updateSelected();
    },

    methods: {
        updateSelected() {
            // on load set value
            let val;
            let pick = (value) =>
                this.options.find(
                    (o) =>
                        // Still needs == because of ugly string validation
                        (this.valueKey === null && value == o) ||
                            (this.valueKey !== null && value == o[this.valueKey]),
                );

            if (this.groups) {
                pick = (value) => {
                    const group = this.options.find((option) => option.options.find((o) => o.value === value))
                    return group.options.find((o) => o.value === value)
                }
            }

            if (this.multiple) {
                val = [];
                if (this.modelValue) {
                    val = this.modelValue
                        .map((v) => pick(v))
                        .filter((v) => v !== undefined);
                }
            } else if (this.modelValue) {
                val = pick(this.modelValue);
            }

            this.selectedObjects = val || null;
            // this.$emit('change', value);
        },
        valueToKey(value) {
            return !this.valueKey ? value : value[this.valueKey];
        },
        valueToLabel(value) {
            return !this.labelKey ? value : value[this.labelKey];
        },
        onClose() {
            this.$emit('close', this.selectedValues, this.id);
        },
        onClear() {
            this.selectedObjects = [];
            this.onChange();
        },
        onChange() {
            this.$emit('input', this.selectedValues);
            this.$emit('update:modelValue', this.selectedValues);
            this.$emit('object', this.selectedValueObjects, this.id);
        },
    },
};
</script>
